/**
 * پرامپت‌های کرالر — سربرگِ «پرامپت‌ها» در ادمین (WHOSUP-215).
 *
 * ═══ صورتِ مسئله ═══
 *
 * کرالر چند جا از مدل می‌پرسد: کدام سایت‌های این شهر ارزشِ کرال کردن دارند،
 * این پیش‌نویس واقعاً رخداد دارد یا نه، این نامِ ساختمان کجای نقشه است. هر
 * کدام یک متنِ پرامپت دارد که در مخزنِ `event_crawler` زندگی می‌کند و از
 * این‌جا اصلاً دیده نمی‌شود. یعنی این سوال‌ها تا امروز بی‌جواب بودند:
 *
 *   • اصلاً چند تا پرامپت داریم و هر کدام چه می‌پرسد؟
 *   • چرا، چه وقت، و با چه شرطی صدا زده می‌شود؟
 *   • چقدر استفاده می‌شود و چقدر خرج برمی‌دارد؟
 *   • آخرین بار چه چیزی فرستاده شد و چه جوابی برگشت؟
 *
 * ═══ چرا حکم این‌جاست و نه در SQL ═══
 *
 * همان تقسیمِ کاری که `sourceTest.ts` و `addressReport.ts` دارند: مایگریشنِ
 * ۰۱۴۲ فقط **واقعیت** می‌دهد (این‌قدر تماس، این‌قدر خطا، این‌قدر میلی‌ثانیه)
 * و اینکه «این پرامپت خراب است» یا «گران است» حکم است و جایش این‌جا:
 *
 *   • آستانه‌ها مدام عوض می‌شوند («چند درصد خطا یعنی خراب»). اگر در SQL
 *     بودند، هر تغییرشان یک مایگریشن می‌خواست.
 *   • این‌طوری با `npm test` آزمودنی است — بی‌شبکه، بی‌دیتابیس.
 *
 * پس این فایل خالص است و هیچ ایمپورتی ندارد.
 *
 * ═══ ⚠ دو منبعِ داده که نباید قاطی شوند ═══
 *
 *   `prompts`  رجیستری — کرالر با `crawl sync-prompts` پُرش می‌کند. متنِ
 *              پرامپت و «چرا/چه وقت/چگونه» فقط از این‌جا می‌آید.
 *   `traces`   ردِ پایی که مدل از قبل در جدول‌های کرالر گذاشته
 *              (`discovery_runs`، `inspections`، `building_geocodes`).
 *              می‌گوید مدل کجا دویده، **ولی پرامپتش را نمی‌داند**.
 *
 * دومی برای این هست که صفحه از روزِ اول حرفی برای گفتن داشته باشد، نه
 * اینکه جای اولی را بگیرد. `readiness` پایین دقیقاً همین را به رابط
 * می‌گوید — و بی آن، صفحه‌ی خالی از «هنوز وصل نشده» قابلِ تشخیص نبود.
 */

// ═══════════ آنچه SQL می‌دهد ═══════════

/** یک شمارشِ ساده‌ی «مقدار → چند بار» — مدل‌ها و نسخه‌ها هر دو این شکل‌اند. */
export interface PromptTally {
  key: string;
  n: number;
}

/** آخرین تماسِ یک پرامپت، خلاصه — بی‌متن. */
export interface PromptLastCall {
  id: number;
  ok: boolean;
  error: string | null;
  model: string | null;
  prompt_version: string | null;
  duration_ms: number | null;
  called_at: string;
}

/**
 * یک ردیفِ فهرست: آنچه رجیستری می‌گوید + آنچه لاگ دیده.
 *
 * ⚠ `registered=false` یعنی این کلید فقط در لاگ هست. آن‌وقت همه‌ی ستون‌های
 * توصیفی تهی‌اند و این **خودش یک یافته است**، نه یک ردیفِ ناقص.
 */
export interface PromptRow {
  key: string;
  registered: boolean;

  title: string | null;
  stage: string | null;
  purpose: string | null;
  when_used: string | null;
  how: string | null;
  cost_note: string | null;
  declared_model: string | null;
  declared_version: string | null;
  source_path: string | null;
  enabled: boolean | null;
  synced_at: string | null;
  template_chars: number | null;

  calls: number;
  calls_d10: number;
  calls_d1: number;
  failed: number;
  failed_d10: number;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  cost_usd_d10: number;
  p50_ms: number | null;
  p95_ms: number | null;
  last_at: string | null;
  /** چند تماس هنوز متنِ واقعی دارد (بقیه را تریگرِ ۰۱۴۲ تهی کرده). */
  with_body: number;

  models: PromptTally[];
  versions: PromptTally[];
  last_call: PromptLastCall | null;
}

/** یک تماس در فهرستِ «تازه‌ترین‌ها» — بی‌متن، برای «الان چه خبر است». */
export interface PromptCallBrief {
  id: number;
  prompt_key: string;
  prompt_version: string | null;
  model: string | null;
  city: string | null;
  target: string | null;
  ok: boolean;
  error: string | null;
  duration_ms: number | null;
  cost_usd: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  called_at: string;
  has_body: boolean;
}

/** همان تماس، این بار با متنِ واقعی — فقط از `admin_prompt_detail`. */
export interface PromptCall extends Omit<PromptCallBrief, 'prompt_key' | 'has_body'> {
  run_id: string | null;
  request: string | null;
  response: string | null;
  truncated: boolean;
  /** متنش تهی شده چون بیست تماسِ تازه‌تر آمده — با «خالی بود» یکی نیست. */
  body_dropped: boolean;
  outcome: Record<string, unknown>;
}

/** رجیستریِ کامل، با متنِ پرامپت. */
export interface PromptDefinition {
  key: string;
  title: string;
  stage: string | null;
  purpose: string | null;
  when_used: string | null;
  how: string | null;
  cost_note: string | null;
  model: string | null;
  version: string | null;
  template: string | null;
  source_path: string | null;
  enabled: boolean;
  synced_at: string;
}

export interface PromptDetail {
  key: string;
  prompt: PromptDefinition | null;
  calls: PromptCall[];
}

/**
 * ردِ پای مدل در یک جدولِ موجود.
 *
 * شکلش عمداً برای هر سه یکی است، با اینکه سه جدولِ متفاوت‌اند: چیزی که
 * رابط از هر سه می‌پرسد یکی است («چند بار، با چه مدلی، چه نسخه‌ای، آخرین
 * بار کِی») و سه کارتِ با سه شکل، مقایسه را ناممکن می‌کرد.
 */
export interface ModelTrace {
  runs: number;
  runs_d10: number;
  last_at: string | null;
  by_model: PromptTally[];
  by_version: PromptTally[];
  recent: Record<string, unknown>[];
  /**
   * متنِ واقعیِ پرامپت در دیتابیس هست یا نه (مایگریشن ۰۱۴۳).
   *
   * ⚠ `false` یعنی **هیچ‌جا ذخیره نشده**، نه «هنوز نرسیده». برای غربالگری
   * همیشه `false` است چون `crawler.inspections` اصلاً ستونی برای متنِ
   * پرسیده‌شده ندارد — و رابط باید همین را بگوید، نه یک «—»ی مبهم.
   */
  has_prompt?: boolean;
  prompt_chars?: number | null;
  /** آدرسِ خودِ متن در دیتابیس: `crawler.discovery_prompts.body`. */
  prompt_origin?: string | null;
  /** کلیدهای مخصوصِ خودِ آن جدول (`candidates`، `usable`، `generated`، …). */
  [extra: string]: unknown;
}

export type TraceKey = 'discovery' | 'inspect' | 'geocode';

/** قالبِ پرامپت، همان‌طور که در دیتابیس نشسته. */
export interface TracePrompt {
  text: string | null;
  version: string | null;
  /** کدام جدول و کدام ستون — آدرسی که می‌شود خودت کوئری‌اش بزنی. */
  origin: string | null;
  added_at: string | null;
}

/**
 * یک تماسِ واقعی.
 *
 * ⚠ `asked` با `TracePrompt.text` یکی نیست: این متنِ **پُرشده**‌ی همان تماس
 * است (با نامِ شهر و فهرستِ مکان‌هایش) و آن یکی قالبِ خام. `null` یعنی آن
 * جدول متنِ پرسیده‌شده را نگه نمی‌دارد.
 */
export interface TraceSample {
  target: string | null;
  model?: string | null;
  version: string | null;
  url?: string | null;
  asked?: string | null;
  answered: unknown;
  confidence?: string | null;
  reasons?: string[] | null;
  at: string;
}

export interface TraceDetail {
  key: TraceKey;
  prompt: TracePrompt | null;
  /** نسخه‌های ثبت‌شده — دیدنِ اینکه قالب کِی عوض شده. */
  versions: { version: string; chars?: number; n?: number; added_at: string }[];
  samples: TraceSample[];
}

export interface PromptsData {
  as_of: string;
  days: number;
  registry: { count: number; enabled: number; last_sync: string | null };
  totals: {
    calls: number;
    calls_d10: number;
    failed_d10: number;
    cost: number;
    cost_d10: number;
    tokens_in: number;
    tokens_out: number;
    last_at: string | null;
  };
  prompts: PromptRow[];
  recent: PromptCallBrief[];
  traces: Partial<Record<TraceKey, ModelTrace>>;
}

// ═══════════ آنچه درباره‌ی ردِ پاها از قبل می‌دانیم ═══════════

/**
 * سه جایی که مدل رد گذاشته، با توضیحِ اینکه هر کدام چه‌کاری می‌کند.
 *
 * ⚠ این متن‌ها **جایگزینِ رجیستری نیستند**. رجیستری از خودِ کرالر می‌آید و
 * متنِ پرامپت را هم دارد؛ این‌ها فقط آن‌قدری‌اند که از این سمتِ مرز
 * می‌شود با اطمینان گفت — از روی ستون‌های همان جدول‌ها و مایگریشن‌های
 * ۰۰۷۶ و ۰۱۴۱. جایی که رجیستری برسد، رابط آن را مقدم می‌کند.
 */
export const TRACE_META: Record<TraceKey, {
  title: string;
  table: string;
  what: string;
  when: string;
  unitLabel: string;
  /** ستونِ خاصِ همان جدول که کنارِ شمارشِ اجرا معنی دارد. */
  extraKey: string;
  extraLabel: string;
  /**
   * آدرسِ فایلِ پرامپت در مخزنِ `event_crawler`.
   *
   * ⚠ **`null` یعنی نمی‌دانیم، و عمداً حدس زده نمی‌شود.** فقط
   * `discovery/claude.py` در همین مخزن مستند شده (`plan/tasks.md`)؛ مسیرِ
   * آن دو تای دیگر تا وقتی کرالر `crawl sync-prompts` را نزده از این سمتِ
   * مرز معلوم نیست. نوشتنِ یک مسیرِ محتمل یعنی فرستادنِ آدم به فایلی که
   * شاید وجود نداشته باشد — که از نگفتن بدتر است. تا آن موقع `prompt_origin`
   * (آدرسِ متن در خودِ دیتابیس) جوابِ به‌دردبخورتری است.
   */
  sourcePath: string | null;
  /** اگر متنِ پرامپت هیچ‌جا ذخیره نمی‌شود، چرا. */
  noPromptWhy?: string;
}> = {
  discovery: {
    title: 'کشفِ منبع',
    table: 'crawler.discovery_runs',
    what: 'از مدل خواسته می‌شود برای یک شهر سایت‌هایی را پیشنهاد کند که '
      + 'تقویمِ رویداد دارند. جوابش به‌شکلِ پیش‌نویس (`draft:{city}:{name}`) '
      + 'در `crawler.source_drafts` می‌نشیند، نه مستقیم در رجیستری.',
    when: 'هر شب، یک شهر — و کدام شهر را `public.crawler_cities_needing_discovery` '
      + 'تعیین می‌کند (مایگریشن ۰۰۹۸). دکمه‌ی «کشف» در سربرگِ منابع هم '
      + 'همین را فوری صدا می‌زند؛ همان کلیک است که پول خرج می‌کند.',
    unitLabel: 'اجرای کشف',
    extraKey: 'candidates',
    extraLabel: 'کاندیدای پیشنهادشده',
    sourcePath: 'discovery/claude.py',
  },
  inspect: {
    title: 'غربالگریِ پیش‌نویس',
    table: 'crawler.inspections',
    what: 'صفحه‌ی یک پیش‌نویس گرفته می‌شود و پرسیده می‌شود آیا واقعاً فهرستِ '
      + 'رویداد است یا نه — حکمش (`verdict`, `looks_usable`, `has_events`) '
      + 'تعیین می‌کند پیش‌نویس به رجیستری ترفیع بگیرد یا رد شود.',
    when: 'هر شب، سقفِ ۲۰ پیش‌نویس، در زنجیره‌ی `discover → inspect → promote`. '
      + 'دکمه‌ی «بررسی» در سربرگِ منابع هم همین را در صف می‌گذارد.',
    unitLabel: 'غربالگری',
    extraKey: 'usable',
    extraLabel: 'به‌دردبخور تشخیص داده شد',
    sourcePath: null,
    noPromptWhy: '`crawler.inspections` ستونی برای متنِ پرسیده‌شده ندارد — '
      + 'برخلافِ دو تای دیگر، از این غربالگری فقط **جواب** ثبت می‌شود. تا '
      + 'وقتی کرالر یا آن ستون را اضافه کند یا `crawl sync-prompts` را بزند، '
      + 'متنِ این پرامپت از هیچ‌جای دیتابیس در نمی‌آید.',
  },
  geocode: {
    title: 'ژئوکدینگِ ساختمان',
    table: 'crawler.building_geocodes',
    what: 'نامِ مکان و آدرسِ آزادِ یک رویداد به مدل داده می‌شود تا بگوید '
      + 'کجای شهر است. جوابش (`answered`) و دلیلش (`reasons`) کنارِ مختصات '
      + 'می‌ماند، و `confidence` می‌گوید قبول شد یا رد.',
    when: 'در خطِ لوله، برای هر مکانِ تازه‌ای که مختصات ندارد. جوابِ هر کلید '
      + 'کش می‌شود، پس یک ساختمان یک بار پرسیده می‌شود نه به‌ازای هر رویداد.',
    unitLabel: 'کلیدِ ژئوکدشده',
    extraKey: 'generated',
    extraLabel: 'مختصات ساخته شد',
    sourcePath: null,
  },
};

/** ترتیبِ خواندن — همان ترتیبی که خودِ خطِ لوله طی می‌کند. */
export const TRACE_ORDER: TraceKey[] = ['discovery', 'inspect', 'geocode'];

// ═══════════ آستانه‌ها ═══════════

/**
 * ⚠ همه‌ی عددهای این بلوک قابلِ بحث‌اند و همین دلیلِ بودنشان این‌جاست، نه
 * در SQL. عوض کردنشان یک ویرایش و یک `npm test` است، نه یک مایگریشن.
 */
/** زیرِ این تعداد تماس در پنجره، نسبتِ خطا عدد نیست — سروصدا است. */
export const MIN_CALLS_FOR_RATE = 3;
/** از این نسبتِ خطا به بالا، «خراب». */
export const FAIL_RATE_BAD = 0.2;
/** از این نسبت به بالا، «مشکوک». */
export const FAIL_RATE_WARN = 0.05;
/** p95ِ کندتر از این، «کند» — تماس با مدل ذاتاً کند است، پس سقف سخاوتمند. */
export const SLOW_P95_MS = 30_000;

// ═══════════ حکم ═══════════

export type PromptVerdict = 'ok' | 'warn' | 'bad' | 'idle' | 'unused';

export const VERDICT_LABEL: Record<PromptVerdict, string> = {
  ok: 'سالم',
  warn: 'مشکوک',
  bad: 'خراب',
  idle: 'خاموش',
  unused: 'بی‌تماس',
};

export const VERDICT_ICON: Record<PromptVerdict, string> = {
  ok: '✅', warn: '⚠️', bad: '❌', idle: '💤', unused: '—',
};

export type NoteTone = 'bad' | 'warn' | 'info';

export interface PromptNote {
  tone: NoteTone;
  text: string;
}

export interface PromptAssessed extends PromptRow {
  verdict: PromptVerdict;
  notes: PromptNote[];
  /** نسبتِ خطا در پنجره؛ `null` یعنی تماسِ کافی برای حرف زدن نبوده. */
  failRate: number | null;
  /** نسخه‌ای که واقعاً بیشتر از همه رفته — با `declared_version` مقایسه می‌شود. */
  usedVersion: string | null;
}

/**
 * وضعیتِ کلِ صفحه — و این مهم‌ترین خروجیِ این فایل است.
 *
 * صفحه‌ی خالی سه دلیلِ کاملاً متفاوت دارد و بدونِ این، هر سه یک شکل‌اند:
 * «کرالر هنوز چیزی نفرستاده»، «فرستاده ولی رجیستری‌اش را نه»، و «رجیستری
 * هست ولی هیچ تماسی ثبت نمی‌شود». اولی یعنی صبر کن، دومی یعنی
 * `sync-prompts` را نزده‌اند، سومی یعنی ثبتِ تماس وصل نشده.
 */
export type Readiness = 'nothing' | 'log-only' | 'registry-only' | 'live';

export const READINESS_NOTE: Record<Readiness, string> = {
  nothing: 'هنوز هیچ‌چیزی از کرالر نرسیده — نه رجیستریِ پرامپت‌ها، نه تماسی. '
    + 'تا وقتی مخزنِ event_crawler `crawl sync-prompts` و ثبتِ تماس را اضافه '
    + 'نکند، این صفحه فقط ردِ پای مدل در جدول‌های موجود را دارد (پایین).',
  'log-only': 'تماس‌ها ثبت می‌شوند ولی رجیستریِ پرامپت‌ها خالی است: کرالر '
    + '`crawl sync-prompts` را نزده. یعنی می‌دانیم چقدر پرسیده شده، ولی نه '
    + 'اینکه چه پرسیده شده.',
  'registry-only': 'رجیستری رسیده ولی هیچ تماسی ثبت نشده. یا مدل از آخرین '
    + 'همگام‌سازی به این‌سو نچرخیده، یا ثبتِ تماس در کرالر وصل نشده.',
  live: 'هم رجیستری رسیده هم تماس‌ها ثبت می‌شوند.',
};

export interface PromptReport {
  readiness: Readiness;
  days: number;
  rows: PromptAssessed[];
  /** کلیدهایی که فقط در لاگ‌اند — همان‌ها که رجیستری جا انداخته. */
  unregistered: PromptAssessed[];
  /** ردِ پاها، به ترتیبِ خطِ لوله و فقط آن‌هایی که واقعاً داده دارند. */
  traces: { key: TraceKey; trace: ModelTrace }[];
  totals: PromptsData['totals'];
  registry: PromptsData['registry'];
  asOf: string;
}

const rate = (failed: number, calls: number): number | null =>
  (calls >= MIN_CALLS_FOR_RATE ? failed / calls : null);

/** پرتکرارترین مقدار در یک شمارش — `null` اگر چیزی نباشد. */
const top = (list: PromptTally[] | null | undefined): string | null => {
  if (!list || !list.length) return null;
  const best = list.reduce((a, b) => (b.n > a.n ? b : a));
  return best.key === '—' ? null : best.key;
};

/**
 * یک ردیف، با حکم و دلیلش.
 *
 * ⚠ ترتیبِ چک‌ها معنی دارد: «ثبت نشده» قبل از هر چیزِ دیگری می‌آید، چون
 * ردیفی که نمی‌دانیم چیست، اینکه چند درصد خطا داده سوالِ دومش است.
 */
export function assessPrompt(r: PromptRow, days: number): PromptAssessed {
  const notes: PromptNote[] = [];
  const failRate = rate(r.failed_d10, r.calls_d10);
  const usedVersion = top(r.versions);

  if (!r.registered) {
    notes.push({
      tone: 'bad',
      text: 'در رجیستری نیست: کرالر این پرامپت را می‌زند ولی ثبتش نکرده. '
        + 'متن و چراییِ آن از این‌جا معلوم نمی‌شود.',
    });
  }

  if (failRate != null && failRate >= FAIL_RATE_BAD) {
    notes.push({
      tone: 'bad',
      text: `${r.failed_d10} تماس از ${r.calls_d10} تماسِ ${days} روزِ اخیر خطا داد.`,
    });
  } else if (failRate != null && failRate >= FAIL_RATE_WARN) {
    notes.push({
      tone: 'warn',
      text: `${r.failed_d10} تماس از ${r.calls_d10} تماسِ ${days} روزِ اخیر خطا داد.`,
    });
  } else if (r.failed_d10 > 0) {
    // زیرِ آستانه ولی صفر هم نیست — خبر است، نه کار.
    notes.push({ tone: 'info', text: `${r.failed_d10} تماسِ خطادار در ${days} روزِ اخیر.` });
  }

  if (r.registered && r.enabled === false) {
    notes.push({ tone: 'info', text: 'در رجیستری خاموش علامت خورده.' });
  }

  // ناهماهنگیِ نسخه: کرالر دیپلوی شده ولی `sync-prompts` نه (یا برعکس). متنی
  // که این صفحه نشان می‌دهد آن‌وقت متنی نیست که واقعاً رفته.
  if (r.registered && usedVersion && r.declared_version
      && usedVersion !== r.declared_version) {
    notes.push({
      tone: 'warn',
      text: `نسخه‌ی رجیستری «${r.declared_version}» است ولی بیشترِ تماس‌ها با `
        + `«${usedVersion}» رفته‌اند — متنِ این صفحه با آنچه اجرا شده یکی نیست.`,
    });
  }

  if (r.registered && r.declared_model && r.models.length
      && !r.models.some((m) => m.key === r.declared_model)) {
    notes.push({
      tone: 'warn',
      text: `رجیستری می‌گوید مدلِ «${r.declared_model}»، ولی هیچ تماسی با آن نرفته.`,
    });
  }

  if (r.models.length > 1) {
    notes.push({
      tone: 'info',
      text: `روی ${r.models.length} مدلِ متفاوت دویده: ${r.models.map((m) => m.key).join('، ')}.`,
    });
  }

  if (r.p95_ms != null && r.p95_ms >= SLOW_P95_MS) {
    notes.push({
      tone: 'warn',
      text: `p95ِ پاسخ ${Math.round(r.p95_ms / 1000)} ثانیه است.`,
    });
  }

  if (r.registered && !r.template_chars) {
    notes.push({ tone: 'warn', text: 'رجیستری متنِ پرامپت را نفرستاده.' });
  }

  if (r.calls > 0 && r.with_body === 0) {
    notes.push({
      tone: 'info',
      text: 'هیچ تماسی متنِ ذخیره‌شده ندارد — یا کرالر متن نمی‌فرستد، یا هر '
        + 'بیست‌تای آخر جابه‌جا شده‌اند.',
    });
  }

  // ⚠ ترتیبِ این شاخه‌ها عمدی است: «خراب» قبل از «بی‌تماس» می‌آید، وگرنه
  // پرامپتی که ثبت نشده یا متنش نیامده، پشتِ برچسبِ بی‌آزارِ «بی‌تماس»
  // پنهان می‌شد — و آن دقیقاً همان ردیفی است که باید دیده شود.
  let verdict: PromptVerdict;
  if (failRate != null && failRate >= FAIL_RATE_BAD) {
    verdict = 'bad';
  } else if (notes.some((n) => n.tone === 'bad')) {
    verdict = 'bad';
  } else if (r.calls === 0) {
    verdict = 'unused';
    notes.push({
      tone: 'info',
      text: 'هیچ تماسی برای این کلید ثبت نشده.',
    });
  } else if (r.calls_d10 === 0) {
    verdict = 'idle';
    notes.push({ tone: 'info', text: `در ${days} روزِ اخیر اصلاً صدا زده نشده.` });
  } else if (notes.some((n) => n.tone === 'warn')) {
    verdict = 'warn';
  } else {
    verdict = 'ok';
  }

  return { ...r, verdict, notes, failRate, usedVersion };
}

/** وضعیتِ کلِ صفحه از روی دو عددی که بیشترین حرف را دارند. */
export function readinessOf(d: PromptsData): Readiness {
  const hasRegistry = d.registry.count > 0;
  const hasCalls = d.totals.calls > 0;
  if (hasRegistry && hasCalls) return 'live';
  if (hasCalls) return 'log-only';
  if (hasRegistry) return 'registry-only';
  return 'nothing';
}

/**
 * گزارشِ کامل — همان چیزی که سربرگ رندر می‌کند.
 *
 * ⚠ ترتیب از SQL می‌آید (پرکارترین اول) و این‌جا **عوض نمی‌شود** مگر برای
 * بالا آوردنِ خراب‌ها: کسی که این صفحه را باز می‌کند دنبالِ کارِ خراب است،
 * و ردیفی که پایینِ فهرستِ بیست‌تایی باشد دیده نمی‌شود.
 */
export function buildPromptReport(d: PromptsData): PromptReport {
  const days = d.days || 10;
  const assessed = (d.prompts ?? []).map((r) => assessPrompt(r, days));

  const RANK: Record<PromptVerdict, number> = {
    bad: 0, warn: 1, ok: 2, idle: 3, unused: 4,
  };
  const rows = [...assessed].sort((a, b) => (
    RANK[a.verdict] - RANK[b.verdict]
    || b.calls_d10 - a.calls_d10
    || b.calls - a.calls
    || a.key.localeCompare(b.key)
  ));

  const traces = TRACE_ORDER
    .map((key) => ({ key, trace: d.traces?.[key] }))
    .filter((x): x is { key: TraceKey; trace: ModelTrace } => !!x.trace);

  return {
    readiness: readinessOf(d),
    days,
    rows,
    unregistered: rows.filter((r) => !r.registered),
    traces,
    totals: d.totals,
    registry: d.registry,
    asOf: d.as_of,
  };
}

// ═══════════ قالب‌بندی ═══════════

/**
 * دلار، با دقتی که به بزرگیِ خودِ عدد بستگی دارد.
 *
 * ⚠ «‎$0.00» برای تماسی که واقعاً پول خرج کرده دروغ است — و در این جدول
 * بیشترِ ردیف‌ها همین‌قدرند. پس زیرِ یک سنت، چهار رقمِ اعشار.
 */
export function fmtUsd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n === 0) return '$0';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

/** توکن — عددِ درشت با K/M، چون ستونِ جدول جا ندارد. */
export function fmtTokens(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/** میلی‌ثانیه، و از یک ثانیه به بالا ثانیه — تماس با مدل ثانیه‌ای است. */
export function fmtMs(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n < 1000) return `${Math.round(n)}ms`;
  return `${(n / 1000).toFixed(1)}s`;
}

/** درصد، یا «—» وقتی تماسِ کافی برای حرف زدن نبوده. */
export function fmtRate(r: number | null | undefined): string {
  if (r == null || !Number.isFinite(r)) return '—';
  return `${Math.round(r * 100)}٪`;
}
